import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@uirouter/core';
import { ProjectSummaryRow } from 'src/app/project-summary/data-access/project-summary.types';
import {
    ManagePdfReportDialogReportOption,
    ManagePdfReportRequestDialogComponent,
    ManagePdfReportRequestPayload
} from '../ui/manage-pdf-report-request-dialog/manage-pdf-report-request-dialog.component';
import { ManagePdfReportSummary } from './manage-pdf-reports.types';
import { ManagePdfReportsDataService } from './manage-pdf-reports-data.service';

@Injectable({ providedIn: 'root' })
export class ManagePdfReportRequestLauncherService {
    constructor(
        private readonly dialog: MatDialog,
        private readonly stateService: StateService,
        private readonly dataService: ManagePdfReportsDataService
    ) { }

    openForProjects(rows: ProjectSummaryRow[], selectedProjectIds: number[]): void {
        const projectIds = this.resolveProjectIds(rows, selectedProjectIds);

        this.dataService.getReports().subscribe((reports) => {
            const dialogRef = this.dialog.open(ManagePdfReportRequestDialogComponent, {
                width: '560px',
                disableClose: false,
                data: {
                    existingReports: this.toDialogReportOptions(reports)
                }
            });

            dialogRef.afterClosed().subscribe((payload?: ManagePdfReportRequestPayload) => {
                if (!payload) {
                    return;
                }

                this.submitPayload(payload, projectIds);
            });
        });
    }

    private submitPayload(payload: ManagePdfReportRequestPayload, projectIds: number[]): void {
        if (payload.mode === 'update' && payload.reportGuid) {
            if (projectIds.length > 0) {
                this.dataService.addProjects(payload.reportGuid, projectIds).subscribe(() => {
                    this.runIfRequestedThenNavigate(payload.reportGuid!, payload.title, payload.runNow);
                });
                return;
            }

            this.dataService.updateReport(payload.reportGuid, {
                reportGuid: payload.reportGuid,
                title: payload.title,
                description: payload.description,
                fileName: payload.fileName
            }).subscribe(() => {
                this.runIfRequestedThenNavigate(payload.reportGuid!, payload.title, payload.runNow);
            });
            return;
        }

        this.dataService.createReport({
            title: payload.title,
            description: payload.description,
            fileName: payload.fileName,
            runImmediately: payload.runNow,
            projectIds
        }).subscribe((created) => {
            const createdReportGuid = this.extractReportGuid(created);
            if (!createdReportGuid) {
                return;
            }

            this.runIfRequestedThenNavigate(createdReportGuid, payload.title, payload.runNow);
        });
    }

    private runIfRequestedThenNavigate(reportGuid: string, reportTitle: string, runNow: boolean): void {
        if (runNow) {
            this.dataService.runReport(reportGuid).subscribe(() => {
                this.navigateToReport(reportGuid, reportTitle);
            });
            return;
        }

        this.navigateToReport(reportGuid, reportTitle);
    }

    private navigateToReport(reportGuid: string, reportTitle: string): void {
        this.stateService.go('reports.pdfReport', {
            guid: reportGuid,
            name: encodeURIComponent(reportTitle || '')
        });
    }

    private extractReportGuid(created: unknown): string | null {
        const item = created as any;
        const raw = item?.reportGuid ?? item?.guid ?? item?.reportId ?? item?.reportID ?? item?.id
            ?? item?.data?.reportGuid ?? item?.data?.guid ?? item?.data?.reportId ?? item?.data?.reportID;
        if (raw == null) {
            return null;
        }

        const guid = String(raw).trim();
        return guid ? guid : null;
    }

    private resolveProjectIds(rows: ProjectSummaryRow[], selectedProjectIds: number[]): number[] {
        const selected = new Set((selectedProjectIds || []).map((value) => Number(value)).filter((id) => id > 0));
        const selectedRows = (rows || []).filter((row) => selected.has(row.projectID));

        if (selectedRows.length > 0) {
            return selectedRows.map((row) => row.projectID);
        }

        return (rows || [])
            .filter((row) => !row.excluded)
            .map((row) => row.projectID)
            .filter((id) => Number(id) > 0);
    }

    private toDialogReportOptions(reports: ManagePdfReportSummary[]): ManagePdfReportDialogReportOption[] {
        return (reports || []).map((item) => ({
            reportGuid: this.resolveReportGuid(item) || '',
            title: String(item.title || item.name || item.reportName || item.reportTitle || ''),
            description: typeof item.description === 'string' ? item.description : '',
            fileName: typeof item.fileName === 'string' ? item.fileName : ''
        })).filter((item) => !!item.reportGuid);
    }

    private resolveReportGuid(report: ManagePdfReportSummary): string | null {
        const raw = report.reportGuid ?? report.guid ?? report.reportGUID ?? report.reportId ?? report.reportID ?? report.id;
        if (raw == null) {
            return null;
        }

        const guid = String(raw).trim();
        return guid ? guid : null;
    }
}
